import {
  ActivityOrderBy,
  Issue,
  IssueMessageType,
  IssueType,
  Severity,
  replaceMentionPlaceholderWithNames,
  useTranslatedText,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { TypeIcon, type TypeVariant } from '../crop-circle/Components/Type';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import type { IssueCardProps } from './issueCard';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useThemeMode } from '../../themes/useThemeMode';

/**
 * Compact Issue Card for asset activity preview:
 * - Type icon (+ reminder icon if applicable)
 * - Issue title
 * - Date
 * - Description (latest message)
 */
export const IssueCardCompact = (props: IssueCardProps) => {
  const { t } = useTranslation();
  // Rendered as a list row inside InfiniteDataGrid, a boundary that (like
  // the Tabs.Screen header render prop - see WorkspacesUser.tsx) doesn't
  // reliably re-render every mounted row just because a distant ancestor's
  // useThemeMode() call re-rendered. Subscribing directly here guarantees
  // every visible row repaints on a live mode flip.
  useThemeMode();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;
    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }
    return titleText;
  };

  // Extract hook calls to comply with Rules of Hooks
  const latestMessage = activity?.latestMessage;
  const { translatedText } = useTranslatedText('text', latestMessage);

  const getLatestMessageText = () => {
    if (!latestMessage) return '';

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const severityToVariant: Record<Severity, TypeVariant> = {
    [Severity.Red]: 'attention',
    [Severity.Yellow]: 'blocker',
    [Severity.Green]: 'done',
    [Severity.Purple]: 'note',
    [Severity.Blue]: 'routines',
  };
  // Campaign Issues always render with the pink variant regardless of
  // severity — mirrors IssueCardBase so the type icon is consistent between
  // the full and compact (asset activity feed) cards.
  const severityVariant =
    activity.type === IssueType.Campaign
      ? 'campaign'
      : severityToVariant[activity.severity] || 'attention';

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return (
    <View
      style={[
        styles.cardCropCircle,
        props.isSelected && styles.cardSelected,
        props.isUnread && styles.cardUnread,
        props.isMentionedInInbox && styles.cardMentioned,
        isHovered && styles.cardHovered,
      ]}
      {...hoverProps}
    >
      <View style={styles.topRow}>
        <View style={styles.titleRow}>
          <TypeIcon variant={severityVariant} size={16} />
          {activity.nextReminderDate && (
            <View style={styles.reminderBadge}>
              <Icon
                name="Clock B"
                size={12}
                color={Colors.role.textSecondary}
              />
            </View>
          )}
          <Text style={styles.title} numberOfLines={1}>
            {getProcessedTitle()}
          </Text>
        </View>

        <Text style={styles.when} numberOfLines={1}>
          {auditData.when}
        </Text>
      </View>

      <Text style={styles.description} numberOfLines={1}>
        {getLatestMessageText()}
      </Text>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  cardCropCircle: {
    width: '100%',
    alignSelf: 'stretch',
    flex: 1,
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: role.surfaceHover,
    marginBottom: tokens.spacing.xs,
    gap: tokens.spacing.xs,
    ...Platform.select({
      android: { elevation: 0 },
      default: {},
    }),
    shadowColor: Colors.black,
    shadowOpacity: 0.02,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  cardSelected: {
    backgroundColor: role.surfaceRaised,
    borderRadius: 0,
  },
  cardUnread: {
    backgroundColor: Colors.unreadBackground,
  },
  cardMentioned: {
    backgroundColor: role.brandTint,
  },
  cardHovered: {
    shadowOpacity: 0.06,
    ...Platform.select({
      android: { elevation: 1 },
      default: {},
    }),
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  reminderBadge: {
    width: 24,
    height: 24,
    borderRadius: tokens.borderRadius.xl,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: role.surfaceHover,
    borderWidth: 1,
    borderColor: role.borderControl,
  },
  title: {
    flex: 1,
    minWidth: 0,
    ...tokens.typography.label.largeEmphasised,
    color: role.textStrong,
  },
  when: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginLeft: 'auto',
  },
  description: {
    ...tokens.typography.label.large,
    color: role.textSecondary,
  },
}));
